// src/runtime/WorkspaceManager/index.ts
// ============================================
// WorkspaceManager - 视图状态唯一来源（冻结版本 v1.0）
// 
// 职责：
//   - 管理当前视图（Single Source of Truth）
//   - 提供视图切换和订阅机制
//   - 不依赖 RuntimeManager，只监听 EventBus
// 
// 冻结声明：此文件为 v1.0 冻结版本，后续只增加功能，不修改现有接口
// ============================================

import { WorkspaceView } from '../../types/workspace';
import { RuntimeEventType, type RuntimeName } from '../../types/runtime';
import type EventBus from '../EventBus';

// ============================================
// Logger 接口（依赖注入）
// ============================================
export interface Logger {
  info(message: string, ...args: unknown[]): void;
  warn(message: string, ...args: unknown[]): void;
  error(message: string, ...args: unknown[]): void;
  debug(message: string, ...args: unknown[]): void;
}

// ============================================
// 类型定义
// ============================================
export type WorkspaceListener = (view: WorkspaceView, source?: RuntimeName) => void;

export interface WorkspaceState {
  currentView: WorkspaceView;
  previousView: WorkspaceView;
  history: WorkspaceView[];
  maxHistory: number;
}

export interface WorkspaceManagerOptions {
  maxHistory?: number;
}

// ============================================
// WorkspaceManager - 核心类
// ============================================
export class WorkspaceManager {
  private static instance: WorkspaceManager | null = null;
  
  // ============================================
  // 私有状态
  // ============================================
  private state: WorkspaceState;
  private listeners: Set<WorkspaceListener> = new Set();
  private eventBus: EventBus | null = null;
  private initialized: boolean = false;
  private logger?: Logger;
  private eventUnsubscribe?: () => void;
  private options: WorkspaceManagerOptions;

  private constructor(options: WorkspaceManagerOptions = {}) {
    this.options = {
      maxHistory: 20,
      ...options,
    };
    this.state = {
      currentView: WorkspaceView.Cockpit,
      previousView: WorkspaceView.Cockpit,
      history: [WorkspaceView.Cockpit],
      maxHistory: this.options.maxHistory || 20,
    };
  }

  // ============================================
  // 单例
  // ============================================
  static getInstance(options?: WorkspaceManagerOptions): WorkspaceManager {
    if (!WorkspaceManager.instance) {
      WorkspaceManager.instance = new WorkspaceManager(options);
    }
    return WorkspaceManager.instance;
  }

  // ============================================
  // 依赖注入
  // ============================================
  setLogger(logger: Logger): void {
    this.logger = logger;
  }

  // ============================================
  // 生命周期
  // ============================================
  initialize(eventBus: EventBus): void {
    if (this.initialized) {
      this.logger?.warn('[WorkspaceManager] Already initialized');
      return;
    }

    this.eventBus = eventBus;
    this.initialized = true;

    // 监听 ViewChanged 事件（返回取消订阅函数）
    this.eventUnsubscribe = this.eventBus.on(
      RuntimeEventType.ViewChanged,
      (event) => {
        const payload = event.payload as { view?: WorkspaceView } | undefined;
        if (payload?.view) {
          this.switchView(payload.view as WorkspaceView, event.source as RuntimeName);
        }
      }
    );

    this.logger?.info('[WorkspaceManager] Initialized');
  }

  /**
   * 销毁 WorkspaceManager（仅用于测试）
   */
  destroy(): void {
    if (this.eventUnsubscribe) {
      this.eventUnsubscribe();
      this.eventUnsubscribe = undefined;
    }
    this.listeners.clear();
    this.eventBus = null;
    this.initialized = false;
    WorkspaceManager.instance = null;
    this.logger?.info('[WorkspaceManager] Destroyed');
  }

  // ============================================
  // 视图操作
  // ============================================
  switchView(view: WorkspaceView, source?: RuntimeName): void {
    if (this.state.currentView === view) {
      this.logger?.debug(`[WorkspaceManager] Already in ${view}`);
      return;
    }

    this.state.previousView = this.state.currentView;
    this.state.currentView = view;
    
    // 更新历史记录（防止重复）
    const lastHistory = this.state.history[this.state.history.length - 1];
    if (lastHistory !== view) {
      this.state.history.push(view);
      if (this.state.history.length > this.state.maxHistory) {
        this.state.history.shift();
      }
    }

    this.logger?.info(
      `[WorkspaceManager] View switched: ${this.state.previousView} → ${this.state.currentView}${
        source ? ` (source: ${source})` : ''
      }`
    );

    this.emit(this.state.currentView, source);
  }

  /**
   * 返回上一个视图
   */
  goBack(): void {
    if (this.state.history.length <= 1) {
      this.logger?.warn('[WorkspaceManager] No history to go back');
      return;
    }
    this.state.history.pop();
    const previousView = this.state.history[this.state.history.length - 1];
    this.switchView(previousView);
  }

  /**
   * 清空历史记录
   */
  clearHistory(): void {
    this.state.history = [this.state.currentView];
    this.logger?.debug('[WorkspaceManager] History cleared');
  }

  // ============================================
  // 查询方法
  // ============================================
  getCurrentView(): WorkspaceView {
    return this.state.currentView;
  }

  getPreviousView(): WorkspaceView {
    return this.state.previousView;
  }

  getHistory(): WorkspaceView[] {
    return [...this.state.history];
  }

  isView(view: WorkspaceView): boolean {
    return this.state.currentView === view;
  }

  getState(): WorkspaceState {
    return {
      ...this.state,
      history: [...this.state.history],
    };
  }

  // ============================================
  // 订阅机制
  // ============================================
  subscribe(listener: WorkspaceListener): () => void {
    this.listeners.add(listener);
    this.logger?.debug('[WorkspaceManager] Listener subscribed');

    // 立即返回当前视图
    listener(this.state.currentView);

    // 返回取消订阅函数
    return () => {
      this.listeners.delete(listener);
      this.logger?.debug('[WorkspaceManager] Listener unsubscribed');
    };
  }

  unsubscribe(listener: WorkspaceListener): void {
    this.listeners.delete(listener);
    this.logger?.debug('[WorkspaceManager] Listener unsubscribed');
  }

  getListenerCount(): number {
    return this.listeners.size;
  }

  // ============================================
  // 私有方法
  // ============================================
  private emit(view: WorkspaceView, source?: RuntimeName): void {
    // 使用快照防止遍历时修改
    const snapshot = [...this.listeners];
    for (const listener of snapshot) {
      try {
        listener(view, source);
      } catch (error) {
        this.logger?.error('[WorkspaceManager] Listener error:', error);
      }
    }
  }

  // ============================================
  // 重置（仅用于测试）
  // ============================================
  reset(): void {
    this.state = {
      currentView: WorkspaceView.Cockpit,
      previousView: WorkspaceView.Cockpit,
      history: [WorkspaceView.Cockpit],
      maxHistory: this.options.maxHistory || 20,
    };
    this.logger?.info('[WorkspaceManager] Reset to Cockpit');
    this.emit(this.state.currentView);
  }
}

// ============================================
// 导出单例
// ============================================
const workspaceManager = WorkspaceManager.getInstance();
export default workspaceManager;